import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiPhone } from "react-icons/fi";
import "./AboutStartNewProject.css";
import mainImage from "../../../../assets/images/allsolutions/main.png";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackStartProject = {
  titleLine1: "Starting a",
  titleLine2: "new project?",
  description:
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions — headquartered in Baghdad.",
  quoteButtonText: "Get a quote",
  quoteButtonLink: "/contactus",
  callbackButtonText: "Request a Call back",
  callbackButtonLink: "/contactus",
  imageAlt: "Worker holding gypsum board",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceProjectPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || {};
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasMeaningfulPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  return Boolean(
    pickString(
      c.title,
      c.titleLine1,
      c.titleLine2,
      c.heading,
      c.description,
      c.body,
      c.text,
      c.quoteButtonText,
      c.callbackButtonText,
      c.callButtonText
    ) ||
      resolveMediaUrl(
        c.mainImage ||
          c.image ||
          c.rightImage ||
          c.mediaUrl ||
          c.MediaUrl
      )
  );
};

const AboutStartNewProject = ({ content }) => {
  const navigate = useNavigate();

  const view = useMemo(() => {
    const payload = coalesceProjectPayload(content);
    if (!hasMeaningfulPayload(payload) || !payload) {
      return { ...fallbackStartProject, imageSrc: mainImage };
    }

    let titleLine1 = pickString(payload.titleLine1, payload.heading);
    let titleLine2 = pickString(payload.titleLine2, payload.subheading);
    const singleTitle = pickString(payload.title);
    if (!titleLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        titleLine1 = parts[0] || "";
        titleLine2 = parts.slice(1).join(" ").trim() || titleLine2;
      } else {
        titleLine1 = singleTitle;
      }
    }

    const imageSrc =
      resolveMediaUrl(
        payload.mainImage ||
          payload.image ||
          payload.rightImage ||
          payload.mediaUrl ||
          payload.MediaUrl ||
          payload.photo
      ) || mainImage;

    return {
      titleLine1: titleLine1 || fallbackStartProject.titleLine1,
      titleLine2:
        titleLine2 ||
        (singleTitle && !singleTitle.includes("\n") ? "" : fallbackStartProject.titleLine2),
      description:
        pickString(payload.description, payload.body, payload.text, payload.summary, payload.content) ||
        fallbackStartProject.description,
      quoteButtonText:
        pickString(payload.quoteButtonText, payload.primaryButtonText, payload.cta1Text) ||
        fallbackStartProject.quoteButtonText,
      quoteButtonLink:
        pickString(payload.quoteButtonLink, payload.primaryButtonLink, payload.cta1Link) ||
        fallbackStartProject.quoteButtonLink,
      callbackButtonText:
        pickString(
          payload.callButtonText,
          payload.callbackButtonText,
          payload.secondaryButtonText,
          payload.cta2Text
        ) ||
        fallbackStartProject.callbackButtonText,
      callbackButtonLink:
        pickString(
          payload.callButtonLink,
          payload.callbackButtonLink,
          payload.secondaryButtonLink,
          payload.cta2Link
        ) ||
        fallbackStartProject.callbackButtonLink,
      imageAlt:
        pickString(payload.mainImageAlt, payload.imageAlt, payload.altText) ||
        fallbackStartProject.imageAlt,
      imageSrc,
    };
  }, [content]);

  return (
    <section className="aboutStartNewProject">
      <div className="aboutStartNewProject-content">
        <h2 className="aboutStartNewProject-title">
          {view.titleLine1}{" "}
          {view.titleLine2 ? <span>{view.titleLine2}</span> : null}
        </h2>

        <p className="aboutStartNewProject-text">
          {view.description}
        </p>

        <div className="aboutStartNewProject-actions">
          <button
            className="aboutStartNewProject-quoteBtn"
            type="button"
            onClick={() => navigate(view.quoteButtonLink)}
          >
            {view.quoteButtonText}
          </button>

          <button
            className="aboutStartNewProject-callBtn"
            type="button"
            onClick={() => navigate(view.callbackButtonLink)}
          >
            <span>{view.callbackButtonText}</span>
            <FiPhone className="aboutStartNewProject-phoneIcon" />
          </button>
        </div>
      </div>

      <div className="aboutStartNewProject-imageWrap">
        <img
          src={view.imageSrc}
          alt={view.imageAlt}
          className="aboutStartNewProject-image"
          onError={(e) => {
            if (e.currentTarget.src !== mainImage) e.currentTarget.src = mainImage;
          }}
        />
      </div>
    </section>
  );
};

export default AboutStartNewProject;